import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { 
  Upload, 
  FileText, 
  X, 
  Loader2, 
  ArrowRight,
  CheckCircle2,
  Trash2,
  Eye,
  FileType,
  Calendar,
  Plus,
  Minus,
  Shield,
  Settings2,
  FileStack,
  History,
  Search,
  RotateCcw,
  Clock,
  Heart
} from "lucide-react";

// DocumentUploadZone as a separate component
function DocumentUploadZone({ 
  index, 
  doc, 
  isUploading, 
  isDragging, 
  onClear, 
  onPreview, 
  onDragOver, 
  onDragLeave, 
  onDrop, 
  onFileSelect,
  onSelectFromHistory,
  formatDate 
}) {
  if (doc) {
    return (
      <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-6 bg-white dark:bg-slate-800">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="font-medium text-slate-900 dark:text-white truncate max-w-[180px]">{doc.filename}</p>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {doc.file_type.toUpperCase()} {doc.uploaded_at && `\u2022 ${formatDate(doc.uploaded_at)}`}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onClear(index)}
            className="text-slate-400 hover:text-red-600"
            data-testid={`clear-doc-${index}`}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
        
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPreview(doc)}
          className="w-full border-slate-200 dark:border-slate-600"
          data-testid={`preview-doc-${index}`}
        >
          <Eye className="w-4 h-4 mr-2" />
          Vista Previa
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div
        className={`upload-zone cursor-pointer min-h-[140px] ${isDragging ? 'dragging border-blue-500 bg-blue-50' : ''}`}
        onDragOver={(e) => onDragOver(e, index)}
        onDragLeave={(e) => onDragLeave(e, index)}
        onDrop={(e) => onDrop(e, index)}
        onClick={() => document.getElementById(`file-input-${index}`).click()}
        data-testid={`upload-zone-${index}`}
      >
        <input
          id={`file-input-${index}`}
          type="file"
          className="hidden"
          accept=".pdf,.doc,.docx"
          onChange={(e) => onFileSelect(e.target.files[0], index)}
        />
        
        {isUploading ? (
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
            <p className="text-slate-600 dark:text-slate-400">Subiendo...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <div className="w-12 h-12 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center">
              <Upload className="w-5 h-5 text-slate-500 dark:text-slate-400" />
            </div>
            <div className="text-center">
              <p className="font-medium text-slate-700 dark:text-slate-300 text-sm">
                Arrastra aquí
              </p>
              <p className="text-xs text-slate-400 mt-1">
                PDF, DOC, DOCX
              </p>
            </div>
          </div>
        )}
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={(e) => { e.stopPropagation(); onSelectFromHistory(index); }}
        className="w-full border-dashed border-slate-300 dark:border-slate-600 text-slate-500 hover:text-blue-600 hover:border-blue-400"
        data-testid={`select-from-history-${index}`}
      >
        <History className="w-4 h-4 mr-2" />
        Seleccionar del historial
      </Button>
    </div>
  );
}

export default function ComparePage({ variant = "generales" }) {
  const isPersonas = variant === "personas";
  // Ramos elegibles para el módulo de Riesgos de Personas
  const PERSONAS_RAMOS = ["salud local", "salud internacional", "vida", "accidentes personales"];
  // Tema visual diferenciado entre variantes
  const theme = isPersonas
    ? {
        // Riesgos de Personas — paleta esmeralda/teal (salud)
        accent: "emerald",
        primaryBtn: "bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white",
        primaryRing: "border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-600 dark:text-emerald-300 dark:hover:bg-emerald-900/30",
        pageBg: "bg-gradient-to-br from-emerald-50/40 via-white to-teal-50/40 dark:from-emerald-950/20 dark:via-slate-900 dark:to-teal-950/10",
        headerBadge: "bg-gradient-to-r from-emerald-100 to-teal-100 text-emerald-800 border-emerald-300 dark:from-emerald-900/40 dark:to-teal-900/40 dark:text-emerald-300 dark:border-emerald-700",
        title: "Comparar Renovaciones",
        subtitle: "Compara renovaciones de pólizas de Riesgos de Personas (Salud, Vida y Accidentes Personales)",
        coberturaCardBorder: "border-emerald-200 dark:border-emerald-800 bg-emerald-50/40 dark:bg-emerald-900/20",
        coberturaShieldText: "text-emerald-600",
        focusRing: "focus:ring-emerald-500",
        plantillasUrl: null, // ocultamos el atajo a Plantillas de Aseguradoras genéricas
      }
    : {
        accent: "blue",
        primaryBtn: "bg-[#0C314C] hover:bg-[#2a4d7a] text-white",
        primaryRing: "border-blue-200 text-blue-700 hover:bg-blue-50",
        pageBg: "bg-slate-50 dark:bg-slate-900",
        headerBadge: "",
        title: "Nueva Comparación",
        subtitle: "Compara hasta 4 pólizas de seguro simultáneamente",
        coberturaCardBorder: "border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/20",
        coberturaShieldText: "text-blue-600",
        focusRing: "focus:ring-blue-500",
        plantillasUrl: "/riesgos-generales/plantillas-aseguradoras",
      };

  const [documents, setDocuments] = useState([]);
  const [numDocs, setNumDocs] = useState(2);
  const [selectedDocs, setSelectedDocs] = useState([null, null, null, null]);
  const [uploading, setUploading] = useState([false, false, false, false]);
  const [comparing, setComparing] = useState(false);
  const [dragOver, setDragOver] = useState([false, false, false, false]);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  // Coberturas fijas state
  const [useCoberturaFija, setUseCoberturaFija] = useState(false);
  const [coberturas, setCoberturas] = useState([]);
  const [selectedCobertura, setSelectedCobertura] = useState(null);
  // Document history state
  const [comparisonHistory, setComparisonHistory] = useState([]);
  const [showDocPicker, setShowDocPicker] = useState(false);
  const [docPickerIndex, setDocPickerIndex] = useState(0);
  const [docSearchTerm, setDocSearchTerm] = useState("");
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();

  // Fetch coberturas for fixed comparison
  const fetchCoberturas = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/coberturas-polizas/all`, getAuthHeaders());
      setCoberturas(res.data);
    } catch (err) {
      console.error("Error fetching coberturas:", err);
    }
  }, [getAuthHeaders]);

  const fetchDocuments = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/documents`, getAuthHeaders());
      setDocuments(res.data);
    } catch (err) {
      console.error("Error fetching documents:", err);
    }
  }, [getAuthHeaders]);

  const fetchComparisonHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/comparisons/history/recent`, getAuthHeaders());
      setComparisonHistory(res.data);
    } catch (err) {
      console.error("Error fetching comparison history:", err);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchCoberturas();
    // En Riesgos de Personas el módulo arranca limpio: no precargamos documentos
    // ni historial de comparaciones del módulo de Riesgos Generales.
    if (!isPersonas) {
      fetchDocuments();
      fetchComparisonHistory();
    }
  }, [fetchDocuments, fetchCoberturas, fetchComparisonHistory, isPersonas]);

  // Handle cobertura fija checkbox change
  const handleCoberturaFijaChange = (checked) => {
    setUseCoberturaFija(checked);
    if (!checked) {
      setSelectedCobertura(null);
    }
  };

  // Handle cobertura selection
  const handleCoberturaSelect = (coberturaId) => {
    const cobertura = coberturas.find(c => c.id === coberturaId);
    setSelectedCobertura(cobertura);
  };

  const handleFileUpload = async (file, index) => {
    if (!file) return;
    
    const ext = file.name.toLowerCase().split('.').pop();
    if (!['pdf', 'doc', 'docx'].includes(ext)) {
      toast.error("Solo se permiten archivos PDF y DOC/DOCX");
      return;
    }

    const newUploading = [...uploading];
    newUploading[index] = true;
    setUploading(newUploading);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API}/documents/upload`, formData, {
        ...getAuthHeaders(),
        headers: {
          ...getAuthHeaders().headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const newSelected = [...selectedDocs];
      newSelected[index] = res.data;
      setSelectedDocs(newSelected);
      
      setDocuments([...documents, res.data]);
      toast.success(`Documento "${file.name}" subido exitosamente`);
    } catch (err) {
      toast.error("Error al subir el documento");
    } finally {
      const newUploading2 = [...uploading];
      newUploading2[index] = false;
      setUploading(newUploading2);
    }
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    const newDragOver = [...dragOver];
    newDragOver[index] = true;
    setDragOver(newDragOver);
  };

  const handleDragLeave = (e, index) => {
    e.preventDefault();
    const newDragOver = [...dragOver];
    newDragOver[index] = false;
    setDragOver(newDragOver);
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    const newDragOver = [...dragOver];
    newDragOver[index] = false;
    setDragOver(newDragOver);
    
    const file = e.dataTransfer.files[0];
    handleFileUpload(file, index);
  };

  const selectExistingDoc = (doc, index) => {
    const newSelected = [...selectedDocs];
    newSelected[index] = doc;
    setSelectedDocs(newSelected);
  };

  const clearSelection = (index) => {
    const newSelected = [...selectedDocs];
    newSelected[index] = null;
    setSelectedDocs(newSelected);
  };

  const openPreview = async (doc) => {
    setPreviewDoc(doc);
    setPreviewLoading(true);
    setTimeout(() => setPreviewLoading(false), 500);
  };

  const addDocument = () => {
    if (numDocs < 4) {
      setNumDocs(numDocs + 1);
    }
  };

  const removeDocument = () => {
    if (numDocs > 2) {
      const newSelected = [...selectedDocs];
      newSelected[numDocs - 1] = null;
      setSelectedDocs(newSelected);
      setNumDocs(numDocs - 1);
    }
  };

  const getSelectedDocsForComparison = () => {
    return selectedDocs.slice(0, numDocs).filter(d => d !== null);
  };

  const startComparison = async () => {
    // If using fixed cobertura, we need at least the cobertura + 1 document
    if (useCoberturaFija) {
      if (!selectedCobertura) {
        toast.error("Por favor seleccione una plantilla de cobertura");
        return;
      }
      
      const docsToCompare = getSelectedDocsForComparison();
      if (docsToCompare.length < 1) {
        toast.error("Por favor seleccione al menos un documento para comparar con la plantilla");
        return;
      }

      setComparing(true);
      try {
        const res = await axios.post(`${API}/comparisons/with-cobertura`, {
          cobertura_id: selectedCobertura.id,
          document_ids: docsToCompare.map(d => d.id)
        }, getAuthHeaders());
        
        toast.success("Comparación con cobertura fija iniciada");
        navigate(`/comparison/${res.data.id}`);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Error al iniciar la comparación");
        setComparing(false);
      }
    } else {
      // Normal comparison flow
      const docsToCompare = getSelectedDocsForComparison();
      
      if (docsToCompare.length < 2) {
        toast.error("Por favor selecciona al menos dos documentos para comparar");
        return;
      }

      const ids = docsToCompare.map(d => d.id);
      if (new Set(ids).size !== ids.length) {
        toast.error("Por favor selecciona documentos diferentes");
        return;
      }

      setComparing(true);
      try {
        const res = await axios.post(`${API}/comparisons/multi`, {
          document_ids: docsToCompare.map(d => d.id)
        }, getAuthHeaders());
        
        toast.success("Comparación iniciada");
        navigate(`/comparison/${res.data.id}`);
      } catch (err) {
        toast.error("Error al iniciar la comparación");
        setComparing(false);
      }
    }
  };

  const deleteDocument = async (docId) => {
    if (!window.confirm("¿Estás seguro de eliminar este documento?")) return;
    try {
      await axios.delete(`${API}/documents/${docId}`, getAuthHeaders());
      setDocuments(documents.filter(d => d.id !== docId));
      
      const newSelected = selectedDocs.map(d => d?.id === docId ? null : d);
      setSelectedDocs(newSelected);
      
      toast.success("Documento eliminado");
    } catch (err) {
      toast.error("Error al eliminar el documento");
    }
  };

  const openDocPicker = (index) => {
    setDocPickerIndex(index);
    setDocSearchTerm("");
    setShowDocPicker(true);
  };

  const selectDocFromPicker = (doc) => {
    const newSelected = [...selectedDocs];
    newSelected[docPickerIndex] = doc;
    setSelectedDocs(newSelected);
    setShowDocPicker(false);
    toast.success(`"${doc.filename}" seleccionado como ${docPickerIndex === 0 ? "Póliza Actual" : docPickerIndex === 1 ? "Renovación" : `Póliza ${docPickerIndex + 1}`}`);
  };

  const reuseComparison = (comp) => {
    // Find the documents from the existing documents list
    const doc1 = documents.find(d => d.id === comp.document1_id);
    const doc2 = documents.find(d => d.id === comp.document2_id);
    
    const newSelected = [null, null, null, null];
    if (doc1) {
      newSelected[0] = doc1;
    } else {
      // Create a minimal doc reference from the comparison
      newSelected[0] = { id: comp.document1_id, filename: comp.document1_name, file_type: "pdf" };
    }
    if (doc2) {
      newSelected[1] = doc2;
    } else {
      newSelected[1] = { id: comp.document2_id, filename: comp.document2_name, file_type: "pdf" };
    }
    
    setSelectedDocs(newSelected);
    setNumDocs(2);
    toast.success("Documentos de la comparación anterior cargados");
  };

  const filteredDocuments = documents.filter(d => 
    d.filename.toLowerCase().includes(docSearchTerm.toLowerCase())
  );

  // Filtrar coberturas según variante: en Riesgos de Personas solo mostramos
  // plantillas asociadas a ramos de personas (Salud Local/Internacional, Vida, AP).
  const filteredCoberturas = coberturas.filter((c) => {
    if (!isPersonas) return true;
    const ramo = (c.nombre_ramo || "").toLowerCase().trim();
    return PERSONAS_RAMOS.some((r) => ramo.includes(r));
  });

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const selectedCount = getSelectedDocsForComparison().length;

  return (
    <div className={`flex min-h-screen ${theme.pageBg}`}>
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid={isPersonas ? "compare-renovaciones-page" : "compare-page"}>
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            {isPersonas && (
              <Badge variant="outline" className={`mb-2 px-3 py-1 text-xs font-semibold tracking-wide ${theme.headerBadge}`}>
                <Heart className="w-3 h-3 mr-1.5 inline" />
                RIESGOS DE PERSONAS
              </Badge>
            )}
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
              {theme.title}
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              {theme.subtitle}
            </p>
          </div>
          
          {/* Add/Remove Document Buttons */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={removeDocument}
              disabled={numDocs <= 2 || useCoberturaFija}
              className="border-slate-200 dark:border-slate-600"
              data-testid="remove-doc-slot"
            >
              <Minus className="w-4 h-4 mr-1" />
              Quitar
            </Button>
            <span className="text-sm text-slate-500 dark:text-slate-400 px-2">
              {useCoberturaFija ? "1 plantilla + 1 documento" : `${numDocs} documentos`}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={addDocument}
              disabled={numDocs >= 4 || useCoberturaFija}
              className="border-slate-200 dark:border-slate-600"
              data-testid="add-doc-slot"
            >
              <Plus className="w-4 h-4 mr-1" />
              Agregar
            </Button>
          </div>
        </div>

        {/* Cobertura Fija Option */}
        <Card className={`mb-6 ${theme.coberturaCardBorder}`}>
          <CardContent className="py-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="cobertura-fija"
                    checked={useCoberturaFija}
                    onCheckedChange={handleCoberturaFijaChange}
                    data-testid="cobertura-fija-checkbox"
                  />
                  <Label 
                    htmlFor="cobertura-fija" 
                    className="text-sm font-medium text-slate-700 dark:text-slate-300 cursor-pointer flex items-center gap-2"
                  >
                    {isPersonas
                      ? <Heart className={`w-4 h-4 ${theme.coberturaShieldText}`} />
                      : <Shield className={`w-4 h-4 ${theme.coberturaShieldText}`} />}
                    Comparar con coberturas fijas
                  </Label>
                </div>

                {useCoberturaFija && (
                  <div className="flex items-center gap-3 ml-4">
                    <Select 
                      value={selectedCobertura?.id || "none"} 
                      onValueChange={(v) => handleCoberturaSelect(v === "none" ? null : v)}
                    >
                      <SelectTrigger className="w-[280px] bg-white dark:bg-slate-800" data-testid="cobertura-select">
                        <SelectValue placeholder="Seleccione plantilla de cobertura" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Seleccione plantilla...</SelectItem>
                        {filteredCoberturas.map((cob) => (
                          <SelectItem key={cob.id} value={cob.id}>
                            {cob.nombre_ramo} ({cob.campos?.length || 0} campos)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {selectedCobertura && (
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        {selectedCobertura.campos?.length || 0} campos
                      </Badge>
                    )}
                  </div>
                )}
              </div>

              {filteredCoberturas.length === 0 && useCoberturaFija && (
                <p className="text-sm text-amber-600 dark:text-amber-400">
                  {isPersonas
                    ? "No hay plantillas de cobertura de Riesgos de Personas (Salud, Vida, AP). Créelas en \"Cobertura de Pólizas\"."
                    : "No hay plantillas de cobertura. Créelas en el menú \"Cobertura de Pólizas\"."}
                </p>
              )}
            </div>

            {selectedCobertura && (
              <div className="mt-4 p-3 bg-white dark:bg-slate-800 rounded-lg border border-blue-200 dark:border-blue-700">
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Vista previa de campos de la plantilla:</p>
                <div className="flex flex-wrap gap-2">
                  {(selectedCobertura.campos || []).slice(0, 6).map((campo, idx) => (
                    <Badge key={idx} variant="secondary" className="text-xs">
                      {campo.nombre}
                    </Badge>
                  ))}
                  {(selectedCobertura.campos?.length || 0) > 6 && (
                    <Badge variant="outline" className="text-xs">
                      +{selectedCobertura.campos.length - 6} más
                    </Badge>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Plantillas de Aseguradoras - Link to configuration (oculto en Riesgos de Personas) */}
        {theme.plantillasUrl && (
        <Card className="mb-6 border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/20">
          <CardContent className="py-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <FileStack className="w-5 h-5 text-amber-600" />
                <div>
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Plantillas de Aseguradoras
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Configura los campos que la IA debe leer de cada aseguradora para mejorar la precisión
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/riesgos-generales/plantillas-aseguradoras')}
                className="border-amber-300 text-amber-700 hover:bg-amber-100 dark:border-amber-600 dark:text-amber-400 dark:hover:bg-amber-900/30"
                data-testid="plantillas-aseguradoras-btn"
              >
                <Settings2 className="w-4 h-4 mr-2" />
                Configurar Plantillas
              </Button>
            </div>
          </CardContent>
        </Card>
        )}

        {/* Upload Section - Dynamic Grid */}
        <div className={`grid gap-6 mb-8 ${
          useCoberturaFija ? 'grid-cols-1 lg:grid-cols-2' :
          numDocs === 2 ? 'grid-cols-1 lg:grid-cols-2' :
          numDocs === 3 ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' :
          'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
        }`}>
          {/* Show selected cobertura as "Póliza Actual" when using fixed cobertura */}
          {useCoberturaFija && (
            <Card className="border-green-200 dark:border-green-800 shadow-sm dark:bg-slate-800 bg-green-50/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-slate-900 dark:text-white font-['Manrope'] flex items-center gap-2">
                  <Shield className="w-4 h-4 text-green-600" />
                  Póliza Actual (Cobertura Fija)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {selectedCobertura ? (
                  <div className="p-4 bg-white dark:bg-slate-700 rounded-lg border border-green-200 dark:border-green-700">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                        <Shield className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white">
                          {selectedCobertura.nombre_ramo}
                        </p>
                        <p className="text-sm text-slate-500">
                          {selectedCobertura.campos?.length || 0} campos de cobertura
                        </p>
                      </div>
                    </div>
                    <div className="space-y-1 text-sm max-h-[120px] overflow-y-auto">
                      {(selectedCobertura.campos || []).slice(0, 5).map((campo, idx) => (
                        <div key={idx} className="flex justify-between text-slate-600 dark:text-slate-400">
                          <span className="truncate">{campo.nombre}</span>
                          <span className="text-slate-400 truncate ml-2">{campo.valor || "-"}</span>
                        </div>
                      ))}
                      {(selectedCobertura.campos?.length || 0) > 5 && (
                        <p className="text-xs text-slate-400">+{selectedCobertura.campos.length - 5} campos más...</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="p-8 text-center text-slate-400 border-2 border-dashed border-slate-200 dark:border-slate-600 rounded-lg">
                    <Shield className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    <p className="text-sm">Seleccione una plantilla de cobertura arriba</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Document upload slots */}
          {Array.from({ length: useCoberturaFija ? 1 : numDocs }).map((_, index) => {
            const actualIndex = useCoberturaFija ? 0 : index;
            return (
            <Card key={actualIndex} className="border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-slate-900 dark:text-white font-['Manrope']">
                  {useCoberturaFija
                    ? "Renovación"
                    : index === 0
                      ? "Póliza Actual"
                      : index === 1
                        ? "Renovación"
                        : `Póliza ${index + 1}`}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <DocumentUploadZone 
                  index={actualIndex}
                  doc={selectedDocs[actualIndex]}
                  isUploading={uploading[actualIndex]}
                  isDragging={dragOver[actualIndex]}
                  onClear={clearSelection}
                  onPreview={openPreview}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onFileSelect={handleFileUpload}
                  onSelectFromHistory={openDocPicker}
                  formatDate={formatDate}
                />
              </CardContent>
            </Card>
          );
          })}
        </div>

        {/* Compare Button */}
        <div className="flex justify-center mb-8">
          <Button
            onClick={startComparison}
            disabled={(useCoberturaFija ? (!selectedCobertura || selectedCount < 1) : selectedCount < 2) || comparing}
            className={`${theme.primaryBtn} px-8 py-6 text-lg shadow-lg`}
            data-testid="start-comparison-button"
          >
            {comparing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                {useCoberturaFija ? "Comparando con plantilla..." : `Analizando ${selectedCount} documentos...`}
              </>
            ) : (
              <>
                {useCoberturaFija 
                  ? `Comparar Plantilla + ${selectedCount} Documento${selectedCount !== 1 ? 's' : ''}`
                  : `Comparar ${selectedCount} Documentos`
                }
                <ArrowRight className="w-5 h-5 ml-2" />
              </>
            )}
          </Button>
        </div>

        {/* Comparaciones Recientes - debajo de la carga de documentos (oculto en Riesgos de Personas) */}
        {!isPersonas && comparisonHistory.length > 0 && (
          <Card className="mb-8 border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800" data-testid="comparison-history-section">
            <CardHeader className="pb-3 border-b border-slate-100 dark:border-slate-700">
              <CardTitle className="text-base font-semibold text-slate-900 dark:text-white font-['Manrope'] flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-500" />
                Comparaciones Recientes ({comparisonHistory.length})
              </CardTitle>
              <CardDescription className="dark:text-slate-400 text-xs">
                Reutiliza documentos de comparaciones anteriores con un clic
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {comparisonHistory.map((comp) => (
                  <div
                    key={comp.id}
                    className="flex items-center justify-between p-3 rounded-lg border border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
                    data-testid={`history-item-${comp.id}`}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-8 h-8 bg-blue-50 dark:bg-blue-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FileText className="w-4 h-4 text-blue-500" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                          {comp.document1_name} <span className="text-slate-400 font-normal">vs</span> {comp.document2_name}
                        </p>
                        <p className="text-xs text-slate-400 truncate">
                          {formatDate(comp.created_at)}
                          {comp.user_name && <span className="text-slate-500 dark:text-slate-400"> · {comp.user_name}</span>}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/comparison/${comp.id}`)}
                        className="text-xs text-slate-500 hover:text-blue-600 px-2"
                        data-testid={`view-comparison-${comp.id}`}
                      >
                        <Eye className="w-3.5 h-3.5 mr-1" />
                        Ver
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => reuseComparison(comp)}
                        className="text-xs border-blue-200 text-blue-600 hover:bg-blue-50 dark:border-blue-700 dark:text-blue-400 px-2"
                        data-testid={`reuse-comparison-${comp.id}`}
                      >
                        <RotateCcw className="w-3.5 h-3.5 mr-1" />
                        Reutilizar
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Document Preview Dialog */}
        <Dialog open={!!previewDoc} onOpenChange={() => setPreviewDoc(null)}>
          <DialogContent className="max-w-4xl max-h-[85vh]">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold font-['Manrope'] flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Vista Previa del Documento
              </DialogTitle>
              <DialogDescription>
                {previewDoc?.filename} {previewDoc?.file_type && `\u2022 ${previewDoc.file_type.toUpperCase()}`}
              </DialogDescription>
            </DialogHeader>
            
            {previewLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : (
              <ScrollArea className="h-[60vh] w-full rounded-md border border-slate-200 p-4 bg-slate-50">
                <div className="prose prose-slate max-w-none">
                  <div className="bg-white p-6 rounded-lg border border-slate-200">
                    <div className="flex items-center gap-3 mb-4 pb-4 border-b border-slate-100">
                      <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                        <FileText className="w-5 h-5 text-slate-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">{previewDoc?.filename}</p>
                        <p className="text-xs text-slate-500">
                          {previewDoc?.uploaded_at && `Subido el ${formatDate(previewDoc.uploaded_at)}`}
                        </p>
                      </div>
                    </div>
                    
                    <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <Eye className="w-4 h-4" />
                      Contenido Extraído
                    </h4>
                    
                    <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed font-mono bg-slate-50 p-4 rounded border border-slate-200 max-h-[400px] overflow-y-auto">
                      {previewDoc?.extracted_text || "No se pudo extraer texto de este documento."}
                    </div>
                  </div>
                </div>
              </ScrollArea>
            )}
            
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
              <Button variant="outline" onClick={() => setPreviewDoc(null)}>
                Cerrar
              </Button>
              <Button
                onClick={() => {
                  const emptyIndex = selectedDocs.slice(0, numDocs).findIndex(d => d === null);
                  if (emptyIndex !== -1) {
                    selectExistingDoc(previewDoc, emptyIndex);
                    setPreviewDoc(null);
                    toast.success(`Documento seleccionado como ${emptyIndex === 0 ? "Póliza Actual" : emptyIndex === 1 ? "Renovación" : `Póliza ${emptyIndex + 1}`}`);
                  } else {
                    toast.info("Todos los slots están ocupados");
                  }
                }}
                className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
              >
                Usar para Comparar
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Document Picker Dialog */}
        <Dialog open={showDocPicker} onOpenChange={setShowDocPicker}>
          <DialogContent className="max-w-lg max-h-[80vh]">
            <DialogHeader>
              <DialogTitle className="text-lg font-bold font-['Manrope'] flex items-center gap-2">
                <History className="w-5 h-5 text-blue-600" />
                Seleccionar Documento
              </DialogTitle>
              <DialogDescription>
                Elige un documento previamente subido para {docPickerIndex === 0 ? "Póliza Actual" : docPickerIndex === 1 ? "Renovación" : `Póliza ${docPickerIndex + 1}`}
              </DialogDescription>
            </DialogHeader>
            
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por nombre..."
                value={docSearchTerm}
                onChange={(e) => setDocSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="doc-picker-search"
              />
            </div>
            
            <ScrollArea className="h-[400px] -mx-2">
              {filteredDocuments.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">
                    {docSearchTerm ? "No se encontraron documentos" : "No hay documentos subidos"}
                  </p>
                </div>
              ) : (
                <div className="space-y-1 px-2">
                  {filteredDocuments.map((doc) => {
                    const isAlreadySelected = selectedDocs.some(s => s?.id === doc.id);
                    return (
                      <button
                        key={doc.id}
                        onClick={() => !isAlreadySelected && selectDocFromPicker(doc)}
                        disabled={isAlreadySelected}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                          isAlreadySelected 
                            ? 'bg-slate-100 dark:bg-slate-700/50 opacity-60 cursor-not-allowed' 
                            : 'hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer'
                        }`}
                        data-testid={`picker-doc-${doc.id}`}
                      >
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          isAlreadySelected ? 'bg-green-100 dark:bg-green-900/30' : 'bg-slate-100 dark:bg-slate-700'
                        }`}>
                          {isAlreadySelected 
                            ? <CheckCircle2 className="w-4 h-4 text-green-600" />
                            : <FileText className="w-4 h-4 text-slate-500" />
                          }
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{doc.filename}</p>
                          <div className="flex items-center gap-2 text-xs text-slate-400">
                            <span>{doc.file_type.toUpperCase()}</span>
                            {doc.uploaded_at && (
                              <>
                                <span className="mx-0.5">&bull;</span>
                                <span>{formatDate(doc.uploaded_at)}</span>
                              </>
                            )}
                          </div>
                        </div>
                        {isAlreadySelected && (
                          <Badge variant="secondary" className="text-xs flex-shrink-0">Ya seleccionado</Badge>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
